import { BadRequest } from 'gportal-grpc/google/rpc/error_details_pb';
import { RegisterUserRequest } from 'gportal-grpc/gportal/api/user/v1/requests_pb';
import useFormValidation from '~/composables/useFormValidation';
import type { FormFieldValidationRuleSet } from '~/composables/useFormValidation/types';
import { GP_ACCESS_TOKEN_COOKIE } from '~/plugins/auth/constants';
import type { RegisterUserFormData } from '../types';
import useReCaptcha from './useReCaptcha';
import useRegisterLocale from './useRegisterLocale';
import {
  FULL_ADDRESS_VALIDATORS,
  MANDATORY_VALIDATORS,
  ZIPCODE_VALIDATORS,
} from './validationRules';
import { Code } from '@connectrpc/connect';
import { useOidcStore } from '~/store/oidc';
import type { GrpcError } from '~/plugins/grpc/universal/types';

export default (
  formData: RegisterUserFormData,
  isCountryZipRequired: ComputedRef<boolean>,
  isCountryFullAddressRequired: ComputedRef<boolean>,
) => {
  const { $i18n, $auth } = useNuxtApp();
  const $grpcUser = useGrpcUser();
  const router = useRouter();

  const authStore = useAuthStore();
  const oidcStore = useOidcStore();

  const { pushErrorNotification, pushSuccessNotification } = useNotifications();

  const isSubmitting = ref<boolean>(false);
  // client side validation
  const validationRules = computed(() => {
    let validators: FormFieldValidationRuleSet<RegisterUserFormData> = {
      ...MANDATORY_VALIDATORS,
    };

    if (unref(isCountryZipRequired)) {
      validators = {
        ...validators,
        ...ZIPCODE_VALIDATORS,
      };
    }

    if (unref(isCountryFullAddressRequired)) {
      validators = {
        ...validators,
        ...ZIPCODE_VALIDATORS,
        ...FULL_ADDRESS_VALIDATORS,
      };
    }

    return validators;
  });
  const {
    invalidFields: clientErrorFields,
    isFormValid,
    userChangedFields,
  } = useFormValidation<RegisterUserFormData>(formData, validationRules);

  // server validation
  const serverErrorFields = ref<
    Partial<Record<keyof RegisterUserFormData, string>>
  >({});

  // recaptcha validation
  const { getReCaptchaToken } = useReCaptcha();
  const validateReCaptcha = async () => {
    // let recaptcha decide
    const reCaptchaToken = await getReCaptchaToken();

    if (reCaptchaToken == null || reCaptchaToken.length === 0) {
      pushErrorNotification({
        title: $i18n.t('register/message/failure/recaptcha/title').toString(),
        message: $i18n
          .t('register/message/failure/recaptcha/message')
          .toString(),
      });
    }

    return reCaptchaToken;
  };

  const getServiceErrorMessage = (error: GrpcError) => {
    if (error.code != null) {
      return `Code: ${error.code} | ${error.message}`;
    }

    return error.message;
  };

  const accessTokenCookie = useCookie(GP_ACCESS_TOKEN_COOKIE);
  const handleRegisterSuccess = async ({
    accessToken,
    idToken,
    refreshToken,
  }) => {
    accessTokenCookie.value = accessToken;

    oidcStore.resetTokens();
    oidcStore.setTokens({ accessToken, idToken, refreshToken });

    try {
      await authStore.fetchUser();
      await authStore.handleUserRegistration();
      authStore.isAuthenticated = true;

      await $auth.init({
        onLoad: 'check-sso',
        token: accessToken,
        idToken,
        refreshToken,
      });

      return new Promise((resolve) => {
        pushSuccessNotification({
          message: $i18n.t('general/login/callback/text').toString(),
          title: $i18n.t('register/message/success').toString(),
          dismissedCallback: () => {
            resolve(true);
          },
        });
      });
    } catch (createCustomerError) {
      pushErrorNotification({
        title: $i18n.t('general/error/text').toString(),
        message: getServiceErrorMessage(createCustomerError),
      });

      accessTokenCookie.value = undefined;
    }

    return Promise.reject();
  };

  const registerLocale = useRegisterLocale();
  const handleRegisterSubmit = async (event) => {
    event.preventDefault();

    // check recaptcha
    const reCaptchaToken = await validateReCaptcha();
    if (reCaptchaToken == null || reCaptchaToken.length === 0) return;

    isSubmitting.value = true;
    const registerUserRequest = new RegisterUserRequest();
    registerUserRequest.email = formData.email;
    registerUserRequest.firstname = formData.firstname;
    registerUserRequest.lastname = formData.lastname;
    registerUserRequest.password = formData.password;
    registerUserRequest.country = formData.country;
    registerUserRequest.newsletter = formData.newsletter;
    registerUserRequest.locale = registerLocale.value;
    registerUserRequest.timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    registerUserRequest.terms = true;
    if (unref(isCountryZipRequired)) {
      registerUserRequest.postalCode = formData.postalCode;
    }

    if (unref(isCountryFullAddressRequired)) {
      registerUserRequest.postalCode = formData.postalCode;
      registerUserRequest.street = formData.street;
      registerUserRequest.locality = formData.locality;
    }

    registerUserRequest.recaptchaToken = reCaptchaToken;

    const { error: registerUserError, response } =
      await $grpcUser.executeApiCallPrimary(
        'registerUser',
        registerUserRequest,
      );

    // error handling
    if (registerUserError != null) {
      const errorDetails = registerUserError.details;

      if (registerUserError.code === Code.AlreadyExists) {
        // user already exists
        pushErrorNotification({
          title: $i18n.t('general/error/text').toString(),
          message: $i18n
            .t('frontend/email-address-is-already-in-use')
            .toString(),
        });
      } else if (errorDetails.length > 0) {
        const badRequestDetails = errorDetails.filter(
          (d) => d.type === 'google.rpc.BadRequest',
        );
        // TODO: will it make sense to move this logic to a separate logic/composable?
        // error with field violations
        badRequestDetails.forEach((d) => {
          const badRequest = new BadRequest().fromBinary(d.value);
          serverErrorFields.value = badRequest.fieldViolations.reduce(
            (errors, fieldViolation) => {
              const violatedField = toCamelCase(fieldViolation.field);
              // we have to filter out the invalid field inputs in the change fields array, in order to give the user the chance to correct his/her input
              userChangedFields.value = userChangedFields.value.filter(
                (field) => field !== violatedField,
              );

              return {
                ...errors,
                [violatedField]: 'form/validation/invalid_field',
              };
            },
            {},
          );
        });
      } else {
        // unknown error
        pushErrorNotification({
          title: $i18n.t('general/error/text').toString(),
          message: getServiceErrorMessage(registerUserError),
        });
      }
    } else if (response != null) {
      const { accessToken: tokenSet } = response;
      try {
        await handleRegisterSuccess(tokenSet);
        await router.push(`/${$i18n.locale.value}`);
      } catch (afterRegisterUserError) {
        // do nothing
      }
    }

    // free up UI
    isSubmitting.value = false;
  };

  // for resetting specific server error field, once a value has been changed by the user in that field
  const changeFieldsWatchHandler = watch(
    () => userChangedFields.value,
    (newValue) => {
      newValue.forEach((field) => {
        delete serverErrorFields.value[field];
      });
    },
  );

  // for displaying error hints in form inputs
  const errorFields = computed(() => ({
    ...clientErrorFields.value,
    ...serverErrorFields.value,
  }));

  onUnmounted(() => {
    changeFieldsWatchHandler();
  });

  return {
    isSubmitting,
    errorFields,
    handleRegisterSubmit,
    isFormValid,
  };
};
